import type { RootContent } from 'hast';
import React, {Fragment, PropsWithChildren, useMemo} from 'react';
import {Components} from 'react-markdown';
import {useMediaQuery} from 'react-responsive';

import TableGraphSwitcher from "@/components/molecule/TableGraphSwitcher/TableGraphSwitcher";
import TableActionBar from '@/components/organism/TableActionBar/TableActionBar';
import { TDataVisualization } from '@/services/threadApi/types';
import { extractLinksFromListSubtree } from '@/utils/urlHandler/urlHandler';

import CardDropdown from '../CardDropdown/CardDropdown';
import LimitedList from '../LimitedList/LimitedList';
import Table from '../Table/Table';
import { StyledTable, StyledTd, StyledTh, StyledThead, StyledTr, TableContainer } from './Style';

const showCard = (
    columnCount: number,
    isMobile: boolean,
    isTablet: boolean,
    isDesktop: boolean,
    isLargeDesktop: boolean,
) => {
  return (
      isMobile || (isTablet && columnCount > 4) || (isDesktop && columnCount > 10) || (isLargeDesktop && columnCount > 20)
  );
};

const getColumnCount = (children: React.ReactNode): number => {
  return React.Children.toArray(children).reduce((count, child) => {
    if (React.isValidElement(child) && child.props.children) {
      return Math.max(count as number, React.Children.count(child.props.children));
    }
    return count;
  }, 0) as number;
};

const shouldShowCard = (children: React.ReactNode) => {
  const columnCount = getColumnCount(children);

  const isMobile = useMediaQuery({ maxWidth: '53.125rem' });
  const isTablet = useMediaQuery({ minWidth: '53.125rem', maxWidth: '64rem' });
  const isDesktop = useMediaQuery({ minWidth: '64rem', maxWidth: '150rem' });
  const isLargeDesktop = useMediaQuery({ minWidth: '150rem' });

  return showCard(columnCount, isMobile, isTablet, isDesktop, isLargeDesktop);
}

type MarkdownRenderersProps = {
  message: string;
  showActions: boolean;
  data_visualization?: TDataVisualization | null;
  messageId: string;
};

const useExtractedLinks = (node: RootContent | undefined) =>
  useMemo(() => extractLinksFromListSubtree(node), [node]);

const MarkdownRenderers = ({
                             message,
                             showActions,
                             data_visualization, // TODO: Reduce prop drilling here since data visualization can be obtained through the messageId
                             messageId,
                           }: MarkdownRenderersProps): Components => {

  return {
    pre: ({ children }: PropsWithChildren) => <Fragment>{children}</Fragment>,
    code: ({ className, children }: PropsWithChildren<{ className?: string }>) => {
      const isJson = className?.includes('language-json');

      if (isJson) {
        try {
          if (!children) {
            throw new Error('No JSON content found');
          }
          const jsonString = Array.isArray(children) ? children.join('') : children.toString();
          const jsonData = JSON.parse(jsonString);

          if (jsonData.table && Array.isArray(jsonData.table)) {
            const card = <CardDropdown>{jsonData.table}</CardDropdown>

            const tableGraphSwitcher =
                <TableGraphSwitcher
                    data={jsonData.table}
                    showActions={showActions}
                    data_visualization={data_visualization ?? null}
                    messageId={messageId}
                    message={message}
                />;

            return shouldShowCard(children) ? card : tableGraphSwitcher;
          }
          return (
              <>
                <TableContainer className="table-responsive">
                  <Table data={jsonData} />
                </TableContainer>
                {showActions ? <TableActionBar message={message} /> : null}
              </>
          );
        } catch (error) {
          console.error('Failed to parse JSON:', error);
          return <pre className={className}>{children}</pre>;
        }
      }
      return <pre className={className}>{children}</pre>;
    },
    // @ts-expect-error — react-markdown renderer props type doesn't match custom PropsWithChildren<{ href: string }>
    a: ({ href, children }: PropsWithChildren<{ href: string }>) => (
        <span className="text-capitalize">
        <a href={href} target="_blank" rel="noopener noreferrer" className="text-color-primary">
          <span className="rioglyph rioglyph-files" aria-hidden="true" />
          <span className="text-size-12-xs text-size-14-ls">{children}</span>
        </a>
      </span>
    ),
    p: ({ children }: PropsWithChildren) => <div className="padding-y-5">{children}</div>,
  
    // #region Lists
    // INFO: Since there's a need to only collapse lists when it is a list of documents, we need to inspect the provided children for the given list

    ul: ({ children, node }) =>  {
      const extractedLinks = useExtractedLinks(node);

      return (<LimitedList urlsContained={extractedLinks.total} elementType="ul">{children}</LimitedList>)
    },

    ol: ({ children, start, node }) => { 
        const extractedLinks = useExtractedLinks(node);

        return (<LimitedList urlsContained={extractedLinks.total} elementType="ol" start={start}>{children}</LimitedList>)
      },

      // #region Lists

    li: ({children}: PropsWithChildren) => (
        <li className="margin-left-20-ls padding-0">{children}</li>
    ),

    table: ({ children }: PropsWithChildren) => {
      return (
          <div className="width-100pct">
            <TableContainer className="table-responsive">
              <StyledTable className="table table-head-filled width-100pct">{children}</StyledTable>
            </TableContainer>
            <TableActionBar message={message} />
          </div>
      );
    },
    th: ({ children }: PropsWithChildren) => {
      return <StyledTh>{children}</StyledTh>;
    },
    td: ({ children }: PropsWithChildren) => {
      return <StyledTd>{children}</StyledTd>;
    },
    tr: ({ children }: PropsWithChildren) => {
      // @ts-expect-error — React.cloneElement expects ReactElement but children may include non-element nodes
      return <StyledTr>{React.Children.map(children, (child, index) => React.cloneElement(child, {index}))}</StyledTr>;
    },
    tbody: ({ children }: PropsWithChildren) => {
      return <tbody>{children}</tbody>;
    },
    thead: ({ children }: PropsWithChildren) => {
      return <StyledThead show={!shouldShowCard(children)}>{children}</StyledThead>;
    },
  };
};

export default MarkdownRenderers;
